import ToolbarButton from "../toolbar/ToolbarButton";
import { useEffect, useState, useContext } from "react";
import { useStrategy } from "../../api/strategy";
import FuelLoad3DTrees from "./FuelLoad3DTrees";
import MapDimFilm from "./MapDimFilm";
import ThreeDShapesIcon from "../icons/ThreeDShapesIcon";
import { StrategyContext } from "../../contexts/StrategyContext";
import { set3DLayerActive } from "../../hooks/use3DLayerActive";
import { useRetainedTilesUrl } from "../../hooks/useRetainedTilesUrl";

/**
 * Standalone "3D Fuel" layer: the 3D trees + 3D fire, split out of the 2D Fuel
 * Load layer so they can be shown with the flat fuel fill turned off. It owns
 * its own copy of the fuel-load vector source (same tiles, separate id) with an
 * invisible fill whose only job is to load the tiles so the tree layer can
 * query them; the trees + fire are drawn by the FuelLoad3DTrees custom layer.
 * Shown whenever this layer's toolbar toggle is on.
 */
const FuelLoad3DLayer = (id: string, name: string) => {
  const { selectedStrategy } = useContext(StrategyContext);
  const { strategy } = useStrategy();
  const [active, setActive] = useState(false);
  const [stale, setStale] = useState(false);

  const strategyData = selectedStrategy
    ? strategy?.data?.[selectedStrategy.id] ?? null
    : null;
  // The live URL goes transiently undefined whenever the strategy recomputes
  // (any mutation bumps strategyRefreshToken, resetting the strategyForYear
  // query). Keep the user's toggle state and the retained tiles through that
  // window — the trees render greyed out (recalculating) instead of vanishing,
  // then `stale` re-adds the source when the fresh URL lands.
  const liveTilesUrl = strategyData?.tiles_url as string | undefined;
  const { tilesUrl, isRetained: recalculating } =
    useRetainedTilesUrl(liveTilesUrl);
  // Retained tiles are still loadable, so the toggle stays usable while the
  // recalculation runs — only a strategy with no tiles at all disables it.
  const dataAvailable = Boolean(tilesUrl);

  useEffect(() => {
    if (!tilesUrl) {
      setStale(false);
      return;
    }
    if (active) setStale(true);
  }, [tilesUrl, active]);

  // Publish toggle state so sibling layers (AssetsLayer) can gate their own 3D
  // output on the "3D Layers" toggle. Reset to off on unmount.
  useEffect(() => {
    set3DLayerActive(active);
  }, [active]);
  useEffect(() => () => set3DLayerActive(false), []);

  const handleToggle = () => {
    setActive((prev) => !prev);
    setStale(true);
  };

  const isVector = active && !!selectedStrategy && !!tilesUrl;
  const vectorSource = isVector
    ? {
        tiles: [tilesUrl],
        minzoom: 6,
        // Tileset is generated to z12; declare the real max so MapLibre
        // overzooms z12 tiles instead of requesting blank z13/z14.
        maxzoom: 12,
        tileSize: 512,
        promoteId: "__id",
      }
    : null;

  // Invisible fill — present only so the vector tiles load (querySourceFeatures
  // needs loaded tiles). The visible output is the 3D trees + fire.
  const layerStyle = {
    id,
    type: "fill",
    "source-layer": "fuel_load",
    paint: {
      "fill-opacity": 0,
    },
    layout: {
      visibility: active ? "visible" : "none",
    },
  };

  const show3d = isVector;

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      toggledOn={active}
      enabled={dataAvailable}
      onClick={handleToggle}
      icon={<ThreeDShapesIcon />}
    />
  );

  return {
    id,
    name,
    type: "vector",
    source: vectorSource,
    rawSource: vectorSource,
    layerStyle: [layerStyle],
    toolbarButton,
    active,
    stale,
    setStale,
    interactiveLayerIds: [],
    customComponent: (
      <>
        <FuelLoad3DTrees
          sourceId={id}
          active={show3d}
          dimFill={false}
          greyed={recalculating}
        />
        <MapDimFilm active={show3d} fuelSourceId={id} />
      </>
    ),
  };
};

export default FuelLoad3DLayer;
